// Harry's watch brief (the "What Harry watches" tab on /harry): topics, competitors, location, the questions customers ask,
// channel switches and extra sites. Draper can set the same brief (set_harry_brief); the owner edits it here.
// Server: server/harry-routes.ts (GET / PUT /api/workspaces/:wsId/harry/brief, POST .../brief/redraft).
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Loader2, Plus, X, RotateCcw, Sparkles, Save, Info } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { HARRY_BRIEF_LIMITS, HARRY_CHANNELS, HARRY_CHANNEL_LABELS, type HarryBrief, type HarryChannel, type HarryBriefDrop } from "@shared/harry-brief";

export interface HarryBriefResponse {
  brief: HarryBrief;
  justDrafted: boolean;
  draftSource: string | null;
  lastScanAt: string | null;
  lastScan: null | {
    at: string; status: string; findings: number; candidates: number; skippedReason: string | null; error: string | null;
    channels: Record<string, { enabled: boolean; queries: number; items: number; skipped: string | null; errors: string[] }>;
  };
  schedule: { mode: "daily_tick" | "scheduler" | "off"; nextScanAfter: string | null; label: string };
  usage: { paidSearch: { used: number; cap: number }; manualScans: { used: number; cap: number }; freeFetchCapPerChannel: number };
  available: { paidSearch: boolean };
}

export const harryBriefKey = (wsId: number) => [`/api/workspaces/${wsId}/harry/brief`];

const CHANNEL_NOTES: Record<HarryChannel, string> = {
  googleNews: "News articles from the past week (free).",
  googleSearch: "Web results from the past week. Uses paid search credits.",
  googleTrends: "Searches that are rising this week. Uses paid search credits.",
  reddit: "Reddit posts from the past week (free).",
  x: "Partial coverage: X posts that Google has indexed. Uses paid search credits.",
};

const AUTHOR: Record<string, string> = { draper: "Set by Draper", owner: "Edited by you", auto: "Drafted from your business profile" };

const SKIP_TEXT: Record<string, string> = { off: "off", no_key: "no search key", daily_cap: "daily limit reached", none_listed: "none listed", newsapi_no_key: "NewsAPI: no key" };

function ChipList({ label, hint, values, onChange, limit, placeholder, testId }: {
  label: string; hint?: string; values: string[]; onChange: (v: string[]) => void; limit: number; placeholder: string; testId: string;
}) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const parts = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    for (const p of parts) if (next.length < limit && !next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-xs font-bold">{label}</Label>
        <span className="text-[10px] text-muted-foreground">{values.length}/{limit}</span>
      </div>
      {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 pr-1 text-[11px] font-medium" data-testid={`${testId}-chip`}>
            {v}
            <button type="button" aria-label={`Remove ${v}`} className="rounded p-0.5 hover:bg-muted" onClick={() => onChange(values.filter((x) => x !== v))}>
              <X size={10} />
            </button>
          </Badge>
        ))}
        {!values.length && <span className="text-[11px] italic text-muted-foreground/60">None yet</span>}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid={`${testId}-input`}
          value={draft}
          disabled={values.length >= limit}
          placeholder={values.length >= limit ? `Limit of ${limit} reached` : placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); } }}
          onPaste={(e) => { const t = e.clipboardData.getData("text"); if (/[,\n]/.test(t)) { e.preventDefault(); add(t); } }}
          className="h-8 text-xs"
        />
        <Button type="button" size="sm" variant="outline" className="h-8" disabled={!draft.trim() || values.length >= limit} onClick={() => add(draft)}>
          <Plus size={12} />
        </Button>
      </div>
    </div>
  );
}

export function HarryWatchBrief({ wsId }: { wsId: number }) {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<HarryBriefResponse>({ queryKey: harryBriefKey(wsId), enabled: !!wsId });
  const [form, setForm] = useState<HarryBrief | null>(null);
  const [questionsText, setQuestionsText] = useState("");
  const [sitesText, setSitesText] = useState("");
  const [dropped, setDropped] = useState<HarryBriefDrop[]>([]);

  const load = (b: HarryBrief) => {
    setForm(b);
    setQuestionsText(b.audienceQuestions.join("\n"));
    setSitesText(b.extraSites.join("\n"));
  };
  useEffect(() => { if (data?.brief) load(data.brief); }, [data?.brief]);

  const payload = useMemo(() => form && ({
    topics: form.topics,
    competitors: form.competitors,
    location: form.location?.trim() ? form.location : null,
    audienceQuestions: questionsText.split("\n").map((s) => s.trim()).filter(Boolean),
    channels: form.channels,
    extraSites: sitesText.split("\n").map((s) => s.trim()).filter(Boolean),
  }), [form, questionsText, sitesText]);

  const dirty = useMemo(() => {
    if (!data?.brief || !payload) return false;
    const b = data.brief;
    return JSON.stringify(payload) !== JSON.stringify({ topics: b.topics, competitors: b.competitors, location: b.location, audienceQuestions: b.audienceQuestions, channels: b.channels, extraSites: b.extraSites });
  }, [data?.brief, payload]);

  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", `/api/workspaces/${wsId}/harry/brief`, payload)).json(),
    onSuccess: (r: { brief: HarryBrief; dropped: HarryBriefDrop[]; changed: string[] }) => {
      setDropped(r.dropped || []);
      load(r.brief);
      queryClient.invalidateQueries({ queryKey: harryBriefKey(wsId) });
      toast({ title: "Harry's brief saved", description: r.dropped?.length ? `${r.dropped.length} item(s) were not kept: see below.` : "The next scan uses it." });
    },
    onError: (e: Error) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  const redraft = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${wsId}/harry/brief/redraft`)).json(),
    onSuccess: (r: { brief: HarryBrief; source: string }) => {
      load(r.brief);
      setDropped([]);
      queryClient.invalidateQueries({ queryKey: harryBriefKey(wsId) });
      toast({ title: "Brief re-drafted", description: r.source === "gemini" ? "Drafted from your business profile." : "Drafted from your profile without the AI model." });
    },
    onError: (e: Error) => toast({ title: "Could not re-draft", description: e.message, variant: "destructive" }),
  });

  if (isLoading || (!form && !error)) {
    return <div className="flex items-center gap-2 py-10 text-xs text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading what Harry watches (a new workspace gets a brief drafted from its profile)…</div>;
  }
  if (error || !form || !data) return <div className="py-10 text-xs text-rose-400">Could not load Harry's brief: {(error as Error)?.message}</div>;

  const L = HARRY_BRIEF_LIMITS;
  const ls = data.lastScan;

  return (
    <div className="space-y-6" data-testid="harry-watch-brief">
      <Card className="border-border/40 bg-card/30">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-lg font-black uppercase tracking-tight">What Harry watches</CardTitle>
              <CardDescription className="text-xs">
                Harry scans these channels for your topics, competitors and customer questions, keeps what is new this week and matters to your business, and tells Draper.
              </CardDescription>
            </div>
            <div className="text-right text-[11px] text-muted-foreground">
              <Badge variant="outline" className="text-[10px]" data-testid="badge-brief-author">{AUTHOR[form.updatedBy] ?? form.updatedBy}</Badge>
              <div className="mt-1">updated {formatDistanceToNow(new Date(form.updatedAt))} ago</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-[11px]">
          <div className="flex flex-wrap items-center gap-2" data-testid="text-scan-schedule">
            <Info size={12} className="text-blue-400" />
            <span className={data.schedule.mode === "off" ? "text-amber-400" : "text-muted-foreground"}>{data.schedule.label}</span>
            {data.schedule.nextScanAfter && <span className="text-muted-foreground">(after {new Date(data.schedule.nextScanAfter).toLocaleString()})</span>}
          </div>
          <div className="text-muted-foreground">
            Last scan: {data.lastScanAt ? `${formatDistanceToNow(new Date(data.lastScanAt))} ago` : "never"}
            {ls && <> · {ls.status === "completed" ? `${ls.findings} finding(s) from ${ls.candidates} new item(s)` : ls.status}{ls.skippedReason ? ` (${ls.skippedReason})` : ""}</>}
            {" · "}Manual scans today: {data.usage.manualScans.used}/{data.usage.manualScans.cap}
            {" · "}Paid searches today: {data.usage.paidSearch.used}/{data.usage.paidSearch.cap}
          </div>
          {ls?.channels && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(ls.channels).map(([k, c]) => (
                <Badge key={k} variant="outline" className={`text-[10px] ${c.errors?.length ? "border-rose-500/40 text-rose-400" : ""}`} title={(c.errors || []).join("\n")}>
                  {(HARRY_CHANNEL_LABELS as Record<string, string>)[k] ?? (k === "extraSites" ? "Extra sites" : k === "sources" ? "Your feeds" : k)}: {c.skipped ? SKIP_TEXT[c.skipped] ?? c.skipped : `${c.items} item(s)`}{c.errors?.length ? `, ${c.errors.length} error(s)` : ""}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-card/30">
        <CardContent className="grid gap-6 pt-6 md:grid-cols-2">
          <ChipList testId="topics" label="Topics" hint="Short search phrases (2-5 words) about your industry, products and local market." values={form.topics} limit={L.topics} placeholder="e.g. sourdough bakery trends" onChange={(topics) => setForm({ ...form, topics })} />
          <ChipList testId="competitors" label="Competitors" hint="Names or websites." values={form.competitors} limit={L.competitors} placeholder="e.g. Rival Bakery or rival.ca" onChange={(competitors) => setForm({ ...form, competitors })} />
          <div className="space-y-2">
            <Label className="text-xs font-bold">Location</Label>
            <p className="text-[11px] text-muted-foreground/70">Sets the country for Google News and search. Leave empty if you serve everywhere.</p>
            <Input data-testid="input-location" className="h-8 text-xs" value={form.location ?? ""} maxLength={L.locationChars} placeholder="e.g. Mississauga, Ontario, Canada" onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label className="text-xs font-bold">Questions your customers ask</Label>
              <span className="text-[10px] text-muted-foreground">one per line, max {L.audienceQuestions}</span>
            </div>
            <Textarea data-testid="input-questions" rows={4} className="text-xs" value={questionsText} placeholder="how much does a custom birthday cake cost" onChange={(e) => setQuestionsText(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-card/30">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-bold">Channels</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {HARRY_CHANNELS.map((ch) => {
            const paid = ch === "googleSearch" || ch === "googleTrends" || ch === "x";
            return (
              <div key={ch} className="flex items-start justify-between gap-3 rounded-xl border border-border/30 p-3">
                <div>
                  <p className="text-xs font-bold">{HARRY_CHANNEL_LABELS[ch]}</p>
                  <p className="text-[11px] text-muted-foreground/70">{CHANNEL_NOTES[ch]}{paid && !data.available.paidSearch ? " Not available: no search key is configured." : ""}</p>
                </div>
                <Switch data-testid={`switch-channel-${ch}`} checked={form.channels[ch]} onCheckedChange={(v) => setForm({ ...form, channels: { ...form.channels, [ch]: v } })} />
              </div>
            );
          })}
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-baseline justify-between">
              <Label className="text-xs font-bold">Extra sites or RSS feeds</Label>
              <span className="text-[10px] text-muted-foreground">one per line, max {L.extraSites}</span>
            </div>
            <p className="text-[11px] text-muted-foreground/70">A feed is read directly; for a web page Harry follows its own RSS link. A page without a feed only counts when it shows an update date.</p>
            <Textarea data-testid="input-sites" rows={3} className="text-xs font-mono" value={sitesText} placeholder="https://www.example-industry-news.com/feed" onChange={(e) => setSitesText(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {dropped.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-[11px]" data-testid="list-dropped">
          <p className="mb-1 font-bold text-amber-400">Not kept:</p>
          {dropped.map((d, i) => <div key={i}>{d.field}: “{d.value}” ({d.reason})</div>)}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="button-save-brief" size="sm" className="gap-2" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </Button>
        <Button size="sm" variant="ghost" className="gap-2" disabled={!dirty} onClick={() => { load(data.brief); setDropped([]); }}>
          <RotateCcw size={14} /> Undo changes
        </Button>
        <Button
          data-testid="button-redraft-brief"
          size="sm" variant="outline" className="ml-auto gap-2" disabled={redraft.isPending}
          onClick={() => { if (confirm("Replace this brief with a new draft from your business profile?")) redraft.mutate(); }}
        >
          {redraft.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Re-draft from profile
        </Button>
      </div>
    </div>
  );
}
